import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { toast } from 'sonner';

/**
 * Modal affichant l'historique des rappels envoyés pour une facture
 * Fonctionne avec la table payment_reminder_logs ou notification_history
 */
const ReminderHistoryModal = ({ invoice, onClose, formatDate }) => {
  const [reminderHistory, setReminderHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableExists, setTableExists] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchReminderHistory();
  }, [invoice.id]);

  const sendNewReminder = async () => {
    try {
      setSending(true);
      console.log('📤 Starting reminder send process for:', invoice.invoice_number);
      toast.info(`Envoi d'un nouveau rappel pour ${invoice.invoice_number}...`);

      // 1. Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('Utilisateur non authentifié');
      }
      
      console.log('👤 Current user:', user.id);
      
      // 2. Get notification settings
      const { data: settings, error: settingsError } = await supabase
        .from('payment_notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        console.warn('⚠️ Settings error:', settingsError);
      }

      const template = settings?.overdue_template || 
        'Paiement en retard : La facture {invoice_number} d\'un montant de {amount} est en retard de {days_overdue} jours.';

      // 3. Build message
      const message = template
        .replace(/{invoice_number}/g, invoice.invoice_number)
        .replace(/{amount}/g, `${invoice.balance_due} ${invoice.currencyCode || 'EUR'}`)
        .replace(/{days_overdue}/g, invoice.daysOverdue?.toString() || '0')
        .replace(/{due_date}/g, formatDate(invoice.due_date))
        .replace(/{client_name}/g, invoice.dentistName);

      const subject = `Rappel de paiement - Facture ${invoice.invoice_number}`;

      console.log('📝 Message prepared:', { subject, message: message.substring(0, 50) + '...' });

      // 4. Determine IDs to save
      const invoiceIdToSave = invoice.itemType === 'payment_phase' ? invoice.parentInvoiceId : invoice.id;
      
      // IMPORTANT: Get the correct user_id from the invoice data
      // The dentist user_id comes from the nested case data, not directly from invoice
      let recipientUserId = null;
      
      // Try multiple sources in order of preference
      if (invoice.client_id) {
        recipientUserId = invoice.client_id;
      } else if (invoice.dentistId) {
        recipientUserId = invoice.dentistId;
      } else if (invoice.user_id) {
        recipientUserId = invoice.user_id;
      }
      
      console.log('🎯 Target IDs:', {
        invoiceIdToSave,
        recipientUserId,
        itemType: invoice.itemType,
        invoice_client_id: invoice.client_id,
        invoice_dentistId: invoice.dentistId,
        invoice_user_id: invoice.user_id,
        ALL_INVOICE_KEYS: Object.keys(invoice)
      });

      // Validate that we have a user_id
      if (!recipientUserId) {
        console.error('❌ Cannot determine recipient user_id!', {
          invoice_keys: Object.keys(invoice),
          invoice_sample: {
            id: invoice.id,
            invoice_number: invoice.invoice_number,
            client_id: invoice.client_id,
            dentistId: invoice.dentistId
          }
        });
        throw new Error('Impossible de déterminer le destinataire du rappel. Vérifiez les données de la facture.');
      }

      // 5. Send dashboard notification
      console.log('💾 Attempting to save to dashboard_notifications...');
      const { data: dashboardData, error: dashboardError } = await supabase
        .from('dashboard_notifications')
        .insert({
          user_id: recipientUserId,
          title: subject,
          message: message,
          type: 'payment_reminder',
          action_url: `/billing-overview?invoice=${invoiceIdToSave}`,
          action_label: 'Voir la facture',
          invoice_id: invoiceIdToSave,
          priority: invoice.severity === 'critical' ? 10 : invoice.severity === 'urgent' ? 7 : 5,
          is_read: false,
          is_dismissed: false
        })
        .select();

      if (dashboardError) {
        console.error('❌ Dashboard notification error:', dashboardError);
      } else {
        console.log('✅ Dashboard notification saved:', dashboardData);
      }

      const dashboardNotified = !dashboardError;

      // 6. Log to notification_history
      console.log('💾 Attempting to save to notification_history...');
      const { data: historyData, error: historyError } = await supabase
        .from('notification_history')
        .insert({
          user_id: recipientUserId,
          invoice_id: invoiceIdToSave,
          notification_type: 'overdue_alert',
          channel: 'dashboard',
          subject: subject,
          message: message,
          status: dashboardNotified ? 'sent' : 'failed',
          error_message: dashboardError?.message || null,
          sent_at: new Date().toISOString(),
          metadata: {
            invoice_number: invoice.invoice_number,
            parent_invoice_number: invoice.parentInvoiceNumber || null,
            days_overdue: invoice.daysOverdue || 0,
            amount: invoice.balance_due,
            currency: invoice.currencyCode,
            sent_from_modal: true,
            is_payment_phase: invoice.itemType === 'payment_phase',
            phase_number: invoice.phase_number || null,
            phase_name: invoice.phase_name || null,
            severity: invoice.severity,
            dentist_name: invoice.dentistName,
            dentist_email: invoice.dentistEmail
          }
        })
        .select();

      if (historyError) {
        console.error('❌ Notification history error:', historyError);
        console.error('❌ Error details:', {
          code: historyError.code,
          message: historyError.message,
          details: historyError.details,
          hint: historyError.hint
        });
      } else {
        console.log('✅ Notification history saved:', historyData);
      }

      // 7. Optionally log to payment_reminder_logs
      try {
        console.log('💾 Attempting to save to payment_reminder_logs (optional)...');
        const { data: logData, error: logError } = await supabase
          .from('payment_reminder_logs')
          .insert({
            invoice_id: invoiceIdToSave,
            payment_phase_id: invoice.itemType === 'payment_phase' ? invoice.id : null,
            sent_at: new Date().toISOString(),
            reminder_type: 'overdue',
            channel: 'dashboard',
            recipient_email: invoice.dentistEmail,
            status: dashboardNotified ? 'sent' : 'failed',
            template_used: 'overdue_template',
            message_content: message,
            sent_by_user_id: user.id,
            error_message: dashboardError?.message || null
          })
          .select();

        if (logError) {
          console.log('ℹ️ payment_reminder_logs error (table may not exist):', logError.code);
        } else {
          console.log('✅ payment_reminder_logs saved:', logData);
        }
      } catch (error) {
        console.log('ℹ️ payment_reminder_logs table not found (optional)');
      }

      // 8. Show result and refresh
      if (dashboardNotified) {
        console.log('✅ Reminder sent successfully!');
        toast.success('✅ Rappel envoyé avec succès !', {
          description: 'Le rappel a été envoyé au tableau de bord du client',
          duration: 5000
        });
        
        // Refresh history to show the new reminder
        console.log('🔄 Refreshing history...');
        await fetchReminderHistory();
      } else {
        console.error('❌ Failed to send reminder');
        toast.error('❌ Échec de l\'envoi du rappel', {
          description: dashboardError?.message || 'Erreur inconnue',
          duration: 7000
        });
      }

    } catch (error) {
      console.error('❌ Fatal error sending reminder:', error);
      toast.error('Erreur lors de l\'envoi du rappel', {
        description: error.message
      });
    } finally {
      setSending(false);
    }
  };

  const fetchReminderHistory = async () => {
    try {
      setLoading(true);

      // Determine which ID to use (for payment phases, use parent invoice ID)
      const invoiceIdToQuery = invoice.itemType === 'payment_phase' 
        ? invoice.parentInvoiceId 
        : invoice.id;

      console.log('🔍 Fetching reminder history for invoice_id:', invoiceIdToQuery);
      console.log('📋 Invoice details:', {
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        itemType: invoice.itemType,
        parentInvoiceId: invoice.parentInvoiceId
      });

      // Try to fetch from notification_history table first
      const { data: notificationData, error: notificationError } = await supabase
        .from('notification_history')
        .select('*')
        .eq('invoice_id', invoiceIdToQuery)
        .order('sent_at', { ascending: false });

      console.log('📊 notification_history query result:', { 
        count: notificationData?.length || 0,
        error: notificationError?.message || null
      });

      // Check if it's a real error (not just empty results)
      if (notificationError && notificationError.code !== 'PGRST116') {
        console.error('❌ Error querying notification_history:', notificationError);
        
        // Try payment_reminder_logs as fallback
        console.log('🔄 Trying payment_reminder_logs as fallback...');
        const { data: reminderData, error: reminderError } = await supabase
          .from('payment_reminder_logs')
          .select('*')
          .or(`invoice_id.eq.${invoiceIdToQuery},payment_phase_id.eq.${invoice.id}`)
          .order('sent_at', { ascending: false });

        if (reminderError && reminderError.code === '42P01') {
          // Table doesn't exist - show message to create it
          console.warn('⚠️ payment_reminder_logs table does not exist');
          setTableExists(false);
          setReminderHistory([]);
        } else if (reminderError) {
          throw reminderError;
        } else {
          console.log('✅ Found data in payment_reminder_logs:', reminderData?.length || 0);
          setTableExists(true);
          setReminderHistory(formatReminderLogs(reminderData || []));
        }
      } else {
        // Successfully queried notification_history (even if empty)
        setTableExists(true);
        const formattedHistory = formatNotificationHistory(notificationData || []);
        console.log('✅ Formatted history:', {
          count: formattedHistory.length,
          items: formattedHistory.map(h => ({ id: h.id, sent_at: h.sent_at, channel: h.channel }))
        });
        setReminderHistory(formattedHistory);
      }
    } catch (error) {
      console.error('❌ Fatal error fetching reminder history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
      setReminderHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Format data from notification_history table
  const formatNotificationHistory = (data) => {
    return data.map(item => ({
      id: item.id,
      sent_at: item.sent_at,
      reminder_type: item.notification_type || 'reminder',
      channel: item.channel || 'dashboard',
      recipient_email: invoice.dentistEmail,
      status: item.status || 'sent',
      template_used: item.subject || 'Rappel de paiement',
      message_content: item.message,
      error_message: item.error_message,
      sent_by: 'Système',
      metadata: item.metadata,
      user_id: item.user_id
    }));
  };

  // Format data from payment_reminder_logs table
  const formatReminderLogs = (data) => {
    return data.map(item => ({
      id: item.id,
      sent_at: item.sent_at,
      reminder_type: item.reminder_type,
      channel: item.channel,
      recipient_email: item.recipient_email,
      recipient_phone: item.recipient_phone,
      status: item.status,
      template_used: item.template_used || 'Template par défaut',
      message_content: item.message_content,
      error_message: item.error_message,
      sent_by: item.sent_by_user_id ? 'Utilisateur' : 'Système automatique',
      sent_by_user_id: item.sent_by_user_id
    }));
  };

  const getReminderTypeConfig = (type) => {
    const configs = {
      email: {
        label: 'Email',
        icon: 'Mail',
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10'
      },
      sms: {
        label: 'SMS',
        icon: 'MessageSquare',
        color: 'text-green-500',
        bgColor: 'bg-green-500/10'
      },
      dashboard: {
        label: 'Notification',
        icon: 'Bell',
        color: 'text-purple-500',
        bgColor: 'bg-purple-500/10'
      },
      system: {
        label: 'Système',
        icon: 'Settings',
        color: 'text-gray-500',
        bgColor: 'bg-gray-500/10'
      }
    };
    return configs[type] || configs.dashboard;
  };

  const getStatusConfig = (status) => {
    const configs = {
      sent: {
        label: 'Envoyé',
        icon: 'CheckCircle',
        color: 'text-success',
        bgColor: 'bg-success/10'
      },
      failed: {
        label: 'Échec',
        icon: 'XCircle',
        color: 'text-destructive',
        bgColor: 'bg-destructive/10'
      },
      pending: {
        label: 'En attente',
        icon: 'Clock',
        color: 'text-warning',
        bgColor: 'bg-warning/10'
      },
      bounced: {
        label: 'Rebondi',
        icon: 'AlertTriangle',
        color: 'text-orange-500',
        bgColor: 'bg-orange-500/10'
      }
    };
    return configs[status] || configs.sent;
  };

  const getReminderTypeLabel = (type) => {
    const labels = {
      reminder: 'Rappel',
      overdue_alert: 'Alerte retard',
      payment_confirmation: 'Confirmation paiement',
      manual: 'Envoi manuel',
      before_due: 'Avant échéance',
      overdue: 'En retard',
      escalation: 'Escalade'
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="History" size={24} className="text-primary" />
              Historique des rappels
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Facture: {invoice.invoice_number}
              {invoice.itemType === 'payment_phase' && ` (${invoice.phase_name})`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Client: {invoice.dentistName} • {invoice.dentistEmail}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            title="Fermer"
            disabled={sending}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={32} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement de l'historique...</span>
            </div>
          ) : tableExists === false ? (
            // Table doesn't exist - show setup instructions
            <div className="text-center py-12">
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-6 mb-6 max-w-2xl mx-auto">
                <Icon name="Database" size={48} className="text-warning mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Table de journalisation non trouvée
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  La table <code className="px-2 py-1 bg-muted rounded text-xs">notification_history</code> n'a pas pu être accédée.
                </p>
                
                <div className="bg-card border border-border rounded-lg p-4 text-left">
                  <p className="text-xs font-semibold text-foreground mb-2">
                    Vérifiez les politiques RLS sur la table notification_history
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Exécutez le script SQL fix_rls_policies.sql fourni dans Supabase.
                  </p>
                </div>
              </div>
            </div>
          ) : reminderHistory.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Aucun rappel envoyé
              </h3>
              <p className="text-muted-foreground mb-4">
                Aucun rappel n'a encore été envoyé pour cette facture
              </p>
              <Button
                variant="default"
                onClick={sendNewReminder}
                disabled={sending}
                iconName={sending ? "Loader" : "Send"}
                iconPosition="left"
                className={sending ? "animate-pulse" : ""}
              >
                {sending ? 'Envoi en cours...' : 'Envoyer le premier rappel de retard'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="BarChart3" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Résumé de l'historique</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Total envoyés</p>
                    <p className="text-2xl font-bold text-foreground">{reminderHistory.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Réussis</p>
                    <p className="text-2xl font-bold text-success">
                      {reminderHistory.filter(r => r.status === 'sent').length}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Échecs</p>
                    <p className="text-2xl font-bold text-destructive">
                      {reminderHistory.filter(r => r.status === 'failed').length}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">En attente</p>
                    <p className="text-2xl font-bold text-warning">
                      {reminderHistory.filter(r => r.status === 'pending').length}
                    </p>
                  </div>
                </div>
                
                {/* Channel breakdown */}
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Canaux utilisés:</p>
                  <div className="flex flex-wrap gap-2">
                    {['email', 'sms', 'dashboard'].map(channel => {
                      const count = reminderHistory.filter(r => r.channel === channel).length;
                      if (count === 0) return null;
                      const config = getReminderTypeConfig(channel);
                      return (
                        <span
                          key={channel}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
                        >
                          <Icon name={config.icon} size={12} />
                          {config.label}: {count}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Clock" size={16} />
                  Chronologie des envois ({reminderHistory.length})
                </h3>

                {reminderHistory.map((reminder, index) => {
                  const typeConfig = getReminderTypeConfig(reminder.channel);
                  const statusConfig = getStatusConfig(reminder.status);

                  return (
                    <div
                      key={reminder.id}
                      className="bg-muted/30 border border-border rounded-lg p-4 hover:shadow-md transition-shadow relative"
                    >
                      {/* Timeline connector */}
                      {index < reminderHistory.length - 1 && (
                        <div className="absolute left-[42px] top-[70px] w-0.5 h-[calc(100%+12px)] bg-border z-0" />
                      )}

                      <div className="flex items-start gap-4 relative z-10">
                        {/* Channel Icon */}
                        <div className={`p-3 rounded-lg ${typeConfig.bgColor} flex-shrink-0`}>
                          <Icon name={typeConfig.icon} size={20} className={typeConfig.color} />
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2 gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-foreground mb-1">
                                {getReminderTypeLabel(reminder.reminder_type)} via {typeConfig.label}
                              </h4>
                              <p className="text-sm text-muted-foreground truncate">
                                {reminder.recipient_email || reminder.recipient_phone || 'Destinataire non spécifié'}
                              </p>
                              {reminder.template_used && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Template: {reminder.template_used}
                                </p>
                              )}
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${statusConfig.bgColor} ${statusConfig.color}`}>
                              <Icon name={statusConfig.icon} size={12} />
                              {statusConfig.label}
                            </span>
                          </div>

                          {/* Message preview */}
                          {reminder.message_content && (
                            <div className="bg-background border border-border rounded-lg p-3 mb-3 text-sm text-foreground">
                              <p className="text-xs font-semibold text-muted-foreground mb-1">Message:</p>
                              <p className="line-clamp-2">{reminder.message_content}</p>
                            </div>
                          )}

                          {/* Error message */}
                          {reminder.error_message && (
                            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-3">
                              <p className="text-xs font-semibold text-destructive mb-1 flex items-center gap-1">
                                <Icon name="AlertCircle" size={12} />
                                Erreur:
                              </p>
                              <p className="text-xs text-destructive">{reminder.error_message}</p>
                            </div>
                          )}

                          {/* Metadata */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground">Date d'envoi</p>
                              <p className="font-medium text-foreground flex items-center gap-1">
                                <Icon name="Calendar" size={12} />
                                {formatDate(reminder.sent_at)}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Heure</p>
                              <p className="font-medium text-foreground flex items-center gap-1">
                                <Icon name="Clock" size={12} />
                                {new Date(reminder.sent_at).toLocaleTimeString('fr-FR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Envoyé par</p>
                              <p className="font-medium text-foreground truncate">
                                {reminder.sent_by}
                              </p>
                            </div>
                          </div>

                          {/* Additional metadata from JSON */}
                          
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={14} />
            <span>
              {tableExists === false 
                ? 'Vérifiez les politiques RLS (voir console)'
                : reminderHistory.length > 0
                ? `${reminderHistory.length} rappel(s) envoyé(s) pour cette facture`
                : 'Les rappels apparaîtront ici une fois envoyés'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              onClick={sendNewReminder}
              disabled={sending || loading}
              iconName={sending ? "Loader" : "Send"}
              iconPosition="left"
              className={sending ? "animate-pulse" : ""}
            >
              {sending ? 'Envoi en cours...' : 'Envoyer un rappel de retard'}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={sending}
            >
              Fermer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReminderHistoryModal;